/* eslint-disable max-len */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpResponse
} from '@angular/common/http';

import {
  EMPTY,
  MonoTypeOperatorFunction,
  Observable,
  UnaryFunction,
  of,
  pipe,
  throwError,
  timer
} from 'rxjs';
import { RetryConfig, catchError, filter, map, tap } from 'rxjs/operators';

import { getRemoteError } from '@app/core/errors/helpers/get-remote-error';
import { RemoteError } from '@app/core/errors/models/remote-error.model';

export interface CatchRemoteErrorOptions<T = unknown> {
  rethrow?: boolean;
  fallbackValue?: T;
  /** @deprecated Use defaultMessage */
  defaultMessageKey?: string;
  defaultMessage?: string;
}

export function filterResponse<T>(): UnaryFunction<
  Observable<HttpEvent<T>>,
  Observable<T>
> {
  return pipe(
    filter((event: HttpEvent<T>) => event.type === HttpEventType.Response),
    map((response: HttpResponse<T>) => response.body)
  );
}

export function uploadProgress<T>(
  cb: (progress: number) => void
): MonoTypeOperatorFunction<HttpEvent<T>> {
  return tap((event: HttpEvent<T>) => {
    if (event.type === HttpEventType.UploadProgress) {
      cb(Math.round((event.loaded * 100) / event.total));
    }
  });
}

export function catchRemoteError<T>(
  onError?: (error: RemoteError) => void,
  options: CatchRemoteErrorOptions<T> = {}
): MonoTypeOperatorFunction<T> {
  return pipe(
    catchError((error) => {
      const remoteError = getRemoteError(error, {
        defaultMessage: options.defaultMessage ?? options.defaultMessageKey
      });

      if (onError) {
        onError(remoteError);
      }

      if (options.rethrow) {
        return throwError(() => error);
      }

      return options.fallbackValue !== undefined
        ? of(options.fallbackValue)
        : EMPTY;
    })
  );
}

export function shouldRetry({
  maxRetryAttempts = 3,
  scalingDuration = 1000,
  excludedStatusCodes = []
}: GenericRetryStrategy = {}): RetryConfig {
  return {
    count: maxRetryAttempts,
    delay(error, retryAttempt) {
      const err = error as HttpErrorResponse;
      if (
        retryAttempt > maxRetryAttempts ||
        excludedStatusCodes.some((statusCode) => statusCode === err.status)
      ) {
        return throwError(() => err);
      }
      return timer(retryAttempt * scalingDuration);
    }
  } as RetryConfig;
}

interface GenericRetryStrategy {
  maxRetryAttempts?: number;
  scalingDuration?: number;
  excludedStatusCodes?: number[];
}
